import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Chip,
  Button,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Person as PersonIcon,
  AccessTime as AccessTimeIcon,
  Business as BusinessIcon,
  CalendarToday as CalendarIcon,
  AccountBalance as AccountBalanceIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { employeeAPI, presenceAPI, adminGroupsAPI } from '../services/api';
import { isBeforeNineRome } from '../utils/entryGate';
import { usePermissions } from '../hooks/usePermissions';
import { isItalianHoliday } from '../utils/holidays';
import DetailedLeaveBalance from './DetailedLeaveBalance';

interface EmployeeInfo {
  id: number;
  nome: string;
  cognome: string;
  matricola: string;
  dipartimento: string;
  qualifica: string;
  ore_settimanali: number;
  giorni_settimanali: number;
  orario_inizio: string;
  orario_fine: string;
  stato: string;
  tipo_contratto: string;
}

interface PresenceStatusData {
  is_checked_in: boolean;
  status?: string;
  last_check_in?: string | null;
  simplified_mode?: boolean;
  double_punch?: boolean;
  punch_step?: string;
  entry_open?: boolean;
}

const EmployeeDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isAdmin, isSuperAdmin } = usePermissions();
  const [employeeInfo, setEmployeeInfo] = useState<EmployeeInfo | null>(null);
  const [loadingInfo, setLoadingInfo] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [notes, setNotes] = useState('');
  const [status, setStatus] = useState<string>('checked_out');
  const [presenceStatus, setPresenceStatus] = useState<PresenceStatusData | null>(null);
  const [loading, setLoading] = useState(false);
  const [hasAdminGroups, setHasAdminGroups] = useState(false);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [leaveBalanceDialogOpen, setLeaveBalanceDialogOpen] = useState(false);
  const [isInAdminManagedGroup, setIsInAdminManagedGroup] = useState(false);
  const [clockTick, setClockTick] = useState(Date.now());

  useEffect(() => {
    loadEmployeeInfo();
    loadPresenceStatus();
    checkAdminGroups();
    checkAdminManagedGroup();
  }, [isAdmin, isSuperAdmin]);

  useEffect(() => {
    const refresh = () => {
      loadPresenceStatus();
      checkAdminManagedGroup();
      setClockTick(Date.now());
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        refresh();
      }
    };
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', onVisible);
    const timer = window.setInterval(refresh, 15000);
    return () => {
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', onVisible);
      window.clearInterval(timer);
    };
  }, []);

  const checkAdminGroups = async () => {
    // Il pulsante admin in questa dashboard serve agli admin di gruppo,
    // non al super admin che ha già come landing la dashboard amministrativa.
    if (!isAdmin || isSuperAdmin) {
      setHasAdminGroups(false);
      setLoadingGroups(false);
      return;
    }

    try {
      setLoadingGroups(true);
      const response = await adminGroupsAPI.getMyGroups();
      const groups = response.groups || [];
      setHasAdminGroups(groups.length > 0);
    } catch (err) {
      setHasAdminGroups(false);
    } finally {
      setLoadingGroups(false);
    }
  };

  const checkAdminManagedGroup = async () => {
    try {
      const response = await adminGroupsAPI.isEmployeeInAdminManagedGroup();
      setIsInAdminManagedGroup(response.is_in_admin_managed_group || false);
    } catch (err) {
      setIsInAdminManagedGroup(false);
    }
  };

  const loadPresenceStatus = async () => {
    try {
      const statusData = await presenceAPI.getStatus();
      setPresenceStatus(statusData);
      if (statusData.status === 'pending_checkout') {
        setStatus('pending_checkout');
        return;
      }
      setStatus(statusData.is_checked_in ? 'checked_in' : 'checked_out');
    } catch (err) {
      // Errore('Errore nel caricamento status presenze:', err);
    }
  };

  const loadEmployeeInfo = async () => {
    try {
      setLoadingInfo(true);
      
      // Recupera i dati del dipendente corrente usando il nuovo endpoint
      const data = await employeeAPI.getMyEmployee();
      const employee = data.employee;
      
      setEmployeeInfo({
        id: employee.id,
        nome: employee.nome,
        cognome: employee.cognome,
        matricola: employee.matricola || 'N/A',
        dipartimento: employee.dipartimento || 'N/A',
        qualifica: employee.qualifica || 'N/A',
        ore_settimanali: employee.ore_settimanali || 0,
        giorni_settimanali: employee.giorni_settimanali || 0,
        orario_inizio: employee.orario_inizio || 'N/A',
        orario_fine: employee.orario_fine || 'N/A',
        stato: employee.stato || 'N/A',
        tipo_contratto: employee.tipo_contratto || 'N/A',
      });
    } catch (err) {
      // Errore('Errore caricamento informazioni dipendente:', err);
    } finally {
      setLoadingInfo(false);
    }
  };

  const handleCheckIn = async () => {
    try {
      setLoading(true);
      setError('');
      setSuccess('');
      await presenceAPI.checkIn(notes);
      setSuccess('Check-in effettuato con successo!');
      setNotes('');
      await loadPresenceStatus();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Errore durante il check-in');
    } finally {
      setLoading(false);
    }
  };

  const handleCheckOut = async () => {
    try {
      setLoading(true);
      setError('');
      setSuccess('');
      await presenceAPI.checkOut(notes);
      setSuccess('Check-out effettuato con successo!');
      setNotes('');
      await loadPresenceStatus();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Errore durante il check-out');
    } finally {
      setLoading(false);
    }
  };

  const handleRegisterDay = async () => {
    try {
      setLoading(true);
      setError('');
      setSuccess('');
      await presenceAPI.registerDay(notes);
      setSuccess('Presenza del giorno registrata con successo!');
      setNotes('');
      await loadPresenceStatus();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Errore durante la registrazione della presenza');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'checked_in': return 'success';
      case 'pending_checkout': return 'warning';
      case 'checked_out': return 'default';
      default: return 'info';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'checked_in': return 'In Servizio';
      case 'pending_checkout': return 'Verifica richiesta';
      case 'checked_out': return 'Fuori Servizio';
      default: return 'Sconosciuto';
    }
  };

  const isTodayHoliday = isItalianHoliday(new Date());
  const hasPendingCheckout = status === 'pending_checkout';
  const pendingCheckoutText = presenceStatus?.last_check_in
    ? `Risulta una timbratura aperta dal ${new Date(presenceStatus.last_check_in).toLocaleString('it-IT')}. Contatta l'amministratore per la chiusura.`
    : 'Risulta una timbratura aperta di un giorno precedente. Contatta l\'amministratore per la chiusura.';

  if (loadingInfo) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h4" component="h1">
          Area personale
        </Typography>
      </Box>

      {hasAdminGroups && (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={() => navigate('/group-dashboard')} disabled={loadingGroups}>
              Vai alla dashboard gruppo
            </Button>
          }
        >
          Stai visualizzando la tua area personale. La home principale del tuo ruolo amministrativo e la dashboard gruppo.
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Informazioni Dipendente */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <PersonIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Informazioni Personali
              </Typography>
              {employeeInfo ? (
                <Box>
                  <Typography variant="body1" gutterBottom>
                    <strong>Nome:</strong> {employeeInfo.nome} {employeeInfo.cognome}
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    <strong>Matricola:</strong> {employeeInfo.matricola}
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    <strong>Dipartimento:</strong> {employeeInfo.dipartimento}
                  </Typography>
                  <Chip
                    label={employeeInfo.stato}
                    color={employeeInfo.stato === 'attivo' ? 'success' : 'default'}
                    size="small"
                    sx={{ mt: 1 }}
                  />
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Informazioni non disponibili
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Orari di Lavoro */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <AccessTimeIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Orari di Lavoro
              </Typography>
              {employeeInfo ? (
                <Box>
                  <Typography variant="body1" gutterBottom>
                    <strong>Ore Settimanali:</strong> {employeeInfo.ore_settimanali}h
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    <strong>Giorni Settimanali:</strong> {employeeInfo.giorni_settimanali}
                  </Typography>
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Orari non disponibili
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Controllo Presenze - nascosto se il dipendente è in un gruppo con gestione admin */}
        {!isInAdminManagedGroup && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  <CalendarIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Controllo Presenze
                </Typography>

                <Box sx={{ mb: 3 }}>
                  <Chip
                    label={getStatusText(status)}
                    color={getStatusColor(status) as any}
                    size="medium"
                    sx={{ mb: 2 }}
                  />
                  {isTodayHoliday && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      Oggi è un giorno festivo: check-in e check-out non sono disponibili.
                    </Alert>
                  )}
                  {hasPendingCheckout && (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                      {pendingCheckoutText}
                    </Alert>
                  )}
                </Box>

                <Box sx={{ mb: 3 }}>
                  <TextField
                    fullWidth
                    label="Note (opzionale)"
                    multiline
                    rows={3}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Aggiungi note per questa rilevazione..."
                  />
                </Box>

                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  {presenceStatus?.simplified_mode ? (
                    <Button
                      variant="contained"
                      color="success"
                      size="large"
                      onClick={handleRegisterDay}
                      disabled={loading || status === 'checked_in' || hasPendingCheckout || isTodayHoliday || presenceStatus?.status === 'completed'}
                      startIcon={<AccessTimeIcon />}
                      sx={{ flex: { xs: '1 1 100%', sm: '0 1 auto' } }}
                    >
                      {loading ? <CircularProgress size={24} /> : (presenceStatus?.status === 'completed' ? 'Presenza di oggi registrata' : 'Registra presenza di oggi')}
                    </Button>
                  ) : presenceStatus?.double_punch ? (
                    <DoublePunchButtons
                      step={presenceStatus.punch_step || 'enter'}
                      loading={loading}
                      blocked={hasPendingCheckout || isTodayHoliday || (!(presenceStatus.entry_open) && isBeforeNineRome(new Date(clockTick)) && (presenceStatus.punch_step || 'enter') === 'enter')}
                      onCheckIn={handleCheckIn}
                      onCheckOut={handleCheckOut}
                    />
                  ) : (
                    <>
                      <Button
                        variant="contained"
                        color="success"
                        size="large"
                        onClick={handleCheckIn}
                        disabled={loading || status === 'checked_in' || hasPendingCheckout || isTodayHoliday || isBeforeNineRome(new Date(clockTick))}
                        startIcon={<AccessTimeIcon />}
                        sx={{ flex: { xs: '1 1 100%', sm: '0 1 auto' } }}
                      >
                        {loading ? <CircularProgress size={24} /> : 'Check-In'}
                      </Button>

                      <Button
                        variant="contained"
                        color="error"
                        size="large"
                        onClick={handleCheckOut}
                        disabled={loading || status === 'checked_out' || hasPendingCheckout || isTodayHoliday}
                        startIcon={<AccessTimeIcon />}
                        sx={{ flex: { xs: '1 1 100%', sm: '0 1 auto' } }}
                      >
                        {loading ? <CircularProgress size={24} /> : 'Check-Out'}
                      </Button>
                    </>
                  )}

                  <Button
                    variant="outlined"
                    color="primary"
                    size="large"
                    onClick={() => setLeaveBalanceDialogOpen(true)}
                    startIcon={<AccountBalanceIcon />}
                    sx={{ flex: { xs: '1 1 100%', sm: '0 1 auto' }, ml: { sm: 'auto' } }}
                  >
                    Saldo Ferie e Permessi
                  </Button>
                </Box>
                {presenceStatus?.simplified_mode && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    Modalità ore giornaliere: con un solo clic registri l&apos;intera giornata prevista, senza check-out.
                  </Typography>
                )}
                {!presenceStatus?.simplified_mode && isBeforeNineRome(new Date(clockTick)) && (presenceStatus?.punch_step || 'enter') === 'enter' && !presenceStatus?.is_checked_in && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    L&apos;entrata si può fare dalle 9:00. Entro 10 minuti l&apos;orario vale completo, dopo i minuti vanno a debito.
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Saldo Ferie - visibile anche per dipendenti in gruppo con gestione admin */}
        {isInAdminManagedGroup && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  <AccountBalanceIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Ferie e Permessi
                </Typography>
                <Alert severity="info" sx={{ mb: 2 }}>
                  Il tuo cartellino è gestito dall'amministratore del gruppo. Il check-in e check-out non sono disponibili.
                </Alert>
                <Button
                  variant="outlined"
                  color="primary"
                  size="large"
                  onClick={() => setLeaveBalanceDialogOpen(true)}
                  startIcon={<AccountBalanceIcon />}
                >
                  Saldo Ferie e Permessi
                </Button>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      {/* Dialog Saldo Ferie e Permessi */}
      <Dialog
        open={leaveBalanceDialogOpen}
        onClose={() => setLeaveBalanceDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <AccountBalanceIcon sx={{ mr: 1 }} />
            Il Mio Saldo Ferie e Permessi
          </Box>
        </DialogTitle>
        <DialogContent>
          <DetailedLeaveBalance />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLeaveBalanceDialogOpen(false)} color="primary">
            Chiudi
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

const DoublePunchButtons: React.FC<{
  step: string;
  loading: boolean;
  blocked: boolean;
  onCheckIn: () => void;
  onCheckOut: () => void;
}> = ({ step, loading, blocked, onCheckIn, onCheckOut }) => {
  if (step === 'done') {
    return <Alert severity="success">Giornata chiusa</Alert>;
  }
  const isOut = step === 'lunch_out' || step === 'final_out';
  const label = step === 'lunch_out'
    ? 'Esci per pranzo'
    : step === 'return'
      ? 'Rientra'
      : step === 'final_out'
        ? 'Esci'
        : 'Entra';
  return (
    <Button
      variant="contained"
      color={isOut ? 'error' : 'success'}
      size="large"
      onClick={isOut ? onCheckOut : onCheckIn}
      disabled={loading || blocked}
      startIcon={<AccessTimeIcon />}
      sx={{ flex: { xs: '1 1 100%', sm: '0 1 auto' } }}
    >
      {loading ? <CircularProgress size={24} /> : label}
    </Button>
  );
};

export default EmployeeDashboard;
